
import { Options, prop, Vue } from "vue-class-component";
import formatPriceRP from "@/app/infrastructures/misc/common-library/FormatPriceRP";
import formatPriceRM from "@/app/infrastructures/misc/common-library/FormatPriceRM";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";
import { EstimationPrice } from "@/domain/entities/Booking";
import { DetailDiscount } from "@/domain/entities/Tariff";
import { CreateBookingV4 } from "@/domain/entities/BookingV4";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

interface TariffDetailRow {
  key: string;
  labelKey: string;
  value: number;
}

class Props {
  isShow = prop<boolean>({ default: false, type: Boolean });
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  estimationPrice = prop<EstimationPrice>({
    required: true,
    type: EstimationPrice
  });
  currencyPrefix = prop<string>({ default: "Rp", type: String });
  // GENESIS-21349 — destination currency for COD Amount / Biaya COD; empty
  // means "same as currencyPrefix" (domestic, zero regression).
  codCurrencyPrefix = prop<string>({ default: "", type: String });
  codCurrencyCode = prop<string>({ default: "", type: String });
  isCod = prop<boolean>({ default: false, type: Boolean });
  isDfod = prop<boolean>({ default: false, type: Boolean });
  itemPrice = prop<number | null>({ default: null });
  woodpacking = prop<boolean>({ default: false, type: Boolean });
  protectionChecked = prop<boolean>({ default: true, type: Boolean });
  isFtz = prop<boolean>({ default: false, type: Boolean });
  appliedVoucherCode = prop<string>({ default: "", type: String });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  hideCodReturnFee = prop<boolean>({ default: false, type: Boolean });
}

@Options({ name: "DetailPembayaranV4" })
class DetailPembayaranV4 extends Vue.with(Props) {
  get isCodOrDfod(): boolean {
    return this.isCod || this.isDfod;
  }

  get baseRows(): TariffDetailRow[] {
    return [
      this.row(
        "publish-shipping-rate",
        "bookingV4.footer.publishShippingRate",
        this.estimationPrice.publishRate +
          this.estimationPrice.shippingSurchargeRate
      ),
      this.row(
        "origin-forward-rate",
        "bookingV4.footer.originForwardRate",
        this.estimationPrice.originDistrictRate
      ),
      this.row(
        "destination-forward-rate",
        "bookingV4.footer.destinationForwardRate",
        this.estimationPrice.destinationDistrictRate
      ),
      this.row(
        "shipping-cost",
        "bookingV4.footer.shippingCost",
        this.estimationPrice.shippingCost
      )
    ];
  }

  get additionalRows(): TariffDetailRow[] {
    return [
      this.row(
        "commodity-surcharge",
        "bookingV4.footer.commoditySurcharge",
        this.estimationPrice.commoditySurcharge
      ),
      this.row(
        "heavyweight-surcharge",
        "bookingV4.footer.heavyweightSurcharge",
        this.estimationPrice.heavyWeightSurcharge
      ),
      this.row(
        "document-surcharge",
        "bookingV4.footer.documentSurcharge",
        this.estimationPrice.documentSurcharge
      ),
      this.row(
        "total-additional-surcharge",
        "bookingV4.footer.totalAdditionalSurcharge",
        this.totalAdditionalSurcharge
      )
    ];
  }

  get totalAdditionalSurcharge(): number {
    return (
      this.estimationPrice.commoditySurcharge +
      this.estimationPrice.heavyWeightSurcharge +
      this.estimationPrice.documentSurcharge
    );
  }

  get promoDiscounts(): DetailDiscount[] {
    const discounts = Array.isArray(this.estimationPrice.listDiscount)
      ? this.estimationPrice.listDiscount
      : [];
    return discounts.filter((item: DetailDiscount) =>
      ["tactical", "campaign"].includes(item.pdcCategory)
    );
  }

  /*
  get hasVoucherDiscount(): boolean {
    return Boolean(
      this.appliedVoucherCode && this.estimationPrice.voucherDiscountAmount > 0
    );
  }
  */

  get hasAnyDiscount(): boolean {
    return this.promoDiscounts.length > 0;
  }

  get codFeeLabel(): string {
    return this.$t(
      this.isDfod
        ? "bookingV4.footer.dfodShippingCod"
        : "bookingV4.footer.codFee"
    ) as string;
  }

  get codReturnFee(): number {
    if (this.estimationPrice.totalTariff <= 0 || this.hideCodReturnFee)
      return 0;
    return (
      (this.estimationPrice.totalTariff - this.estimationPrice.codFee) * 0.5
    );
  }

  get showOriginalStrikethrough(): boolean {
    return (
      !this.isCodOrDfod &&
      this.hasAnyDiscount &&
      this.originalValue > this.finalValue
    );
  }

  get originalValue(): number {
    return this.isCodOrDfod
      ? this.estimationPrice.codAmount
      : this.estimationPrice.totalTariff;
  }

  get finalValue(): number {
    if (this.isCodOrDfod) return this.estimationPrice.codAmount;
    return (
      this.estimationPrice.finalPrice ??
      this.estimationPrice.totalTariffAfterDiscount ??
      this.estimationPrice.totalTariff
    );
  }

  /*
  get totalBeforeVoucher(): number {
    return (
      this.estimationPrice.totalTariffAfterDiscount ??
      this.estimationPrice.totalTariff
    );
  }
  */

  get totalLabel(): string {
    if (this.isCodOrDfod) {
      return this.$t("bookingV4.footer.recipientPayable") as string;
    }
    // if (this.hasVoucherDiscount) {
    //   return this.$t("bookingV4.footer.totalAfterVoucher") as string;
    // }
    return this.$t("bookingV4.footer.totalTariff") as string;
  }

  row(key: string, labelKey: string, value: number): TariffDetailRow {
    return { key, labelKey, value: value || 0 };
  }

  promoLabel(promo: DetailDiscount): string {
    return this.$t(
      promo.pdcCategory === "tactical"
        ? "bookingV4.footer.promo.tactical"
        : "bookingV4.footer.promo.campaign"
    ) as string;
  }

  currency(value: number): string {
    return this.currencyPrefix === "RM"
      ? formatPriceRM(value || 0)
      : formatPriceRP(value || 0);
  }

  get resolvedCodCurrencyCode(): string {
    return (
      this.codCurrencyCode || (this.currencyPrefix === "RM" ? "MYR" : "IDR")
    );
  }

  // COD Amount / Biaya COD render in destination currency for cross-country
  // COD (GENESIS-21349) — codReturnFee and every other row above stay on
  // origin currency via currency(), same convention as the v3 sibling
  // (create-booking-v3/.../footer/components/detail-estimation.vue).
  currencyCod(value: number): string {
    if (this.isCrossCountryCod) {
      const hasFraction = (num: number) => {
        return typeof num === 'number' && !Number.isInteger(num);
      }
      const codAmount = formatByCountry(value, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code,
        fractionDigits: hasFraction(value) ? 2 : 0,
        minFractionDigits: hasFraction(value) ? 2 : 0
      });

      return `${this.modelValue.receiver.destinationCountry.currencySymbol}${codAmount}`;
    }

    const config = configPrice[this.resolvedCodCurrencyCode] ?? configPrice.IDR;
    if (!value) return `${config.prefix}0`;
    return `${config.prefix}${value.toLocaleString(config.locales)}`;
  }
}

export default DetailPembayaranV4;
